import {
  useEffect,
  useState,
} from 'react';

import { Dropdown } from 'react-bootstrap';
import { shallowEqual, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import LoadingContent from 'app/modules/loading/LoadingContent';

import { formatDate } from 'app/services/dates';
import { fetchBranchLessons, getLessonPath } from 'app/services/lessons';

import LessonItem from './LessonItem';

type LessonListProps = {
  branch: any,
}

const LessonList = ({ branch }: LessonListProps) => {
  const { auth } = useSelector((state: any) => ({
    auth: state.auth,
  }), shallowEqual);

  const navigate = useNavigate();
  const location = useLocation();

  const [lessons, setLessons] = useState<any>([]);
  const [fields, setFields] = useState<any>([]);
  const [selectedField, setSelectedField] = useState<string>('');
  const [onlyMine, setOnlyMine] = useState<boolean>(false);
  const [onlyAvailable, setOnlyAvailable] = useState<boolean>(true);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  const branchId = branch?.attributes?.partner_id;

  useEffect(() => {
    if (!branchId) {
      return;
    }

    setLoading(true);

    fetchBranchLessons(branchId, {
      fieldId: selectedField ? [selectedField] : [],
      onlyMine: onlyMine,
    }).then((data: any) => {
      setLessons(data);

      // the field filter options come from the unfiltered list
      if (!selectedField && !onlyMine) {
        const uniqueFields = new Map();
        data.forEach((lesson: any) => uniqueFields.set(lesson.relationships.field.id, lesson.relationships.field));
        setFields(Array.from(uniqueFields.values()).sort((a: any, b: any) =>
          a.attributes.name.localeCompare(b.attributes.name, 'pt-BR', { numeric: true, sensitivity: 'base' })
        ));
      }
    }).catch(() => {
      setLessons([]);
    }).finally(() => {
      setLoading(false);
    });
  }, [branchId, selectedField, onlyMine, auth?.user?.id]);

  const toggleOnlyMine = () => {
    if (!auth.user) {
      navigate({}, { state: { login: location } });
      return;
    }

    setOnlyMine(!onlyMine);
  }

  // days and availability are filtered only in the frontend
  const dates: string[] = Array.from(new Set<string>(lessons.map((lesson: any) => lesson.attributes.date)));

  // the lessons the client participates in are always shown, even when the class is full
  const filteredLessons = lessons.filter((lesson: any) =>
    (!onlyAvailable || lesson.attributes.has_available_spots || lesson.attributes.is_participant) &&
    (!selectedDate || lesson.attributes.date === selectedDate)
  );

  const lessonsByDate = filteredLessons.reduce((groups: any, lesson: any) => {
    const date = lesson.attributes.date;

    groups[date] = [...(groups[date] ?? []), lesson];

    return groups;
  }, {});

  return (
    <div className='card mb-5 mb-xl-10'>
      <div className='card-body pt-12'>
        <div className='mb-10 text-center'>
          <h1 className='mb-3'>
            Grade de aulas
          </h1>
          <div className='text-muted fw-semibold fs-5'>
            Confira as aulas dos próximos 7 dias.
          </div>
          <div className='text-muted fw-semibold fs-5'>
            Use seu crédito de reposição para entrar em uma aula com vaga.
          </div>
        </div>

        <div className='mb-10'>
          {dates.length > 1 && <div className='d-flex flex-nowrap gap-2 overflow-auto pb-2 mb-3'>
            <span
              className={`btn btn-sm rounded-pill text-nowrap flex-shrink-0 px-4 ${selectedDate === '' ? 'btn-primary' : 'btn-light'}`}
              onClick={() => setSelectedDate('')}
            >
              Todos os dias
            </span>

            {dates.map((date: string) => <span
              key={`date-filter-${date}`}
              className={`btn btn-sm rounded-pill text-nowrap flex-shrink-0 px-4 ${selectedDate === date ? 'btn-primary' : 'btn-light'}`}
              onClick={() => setSelectedDate(date)}
            >
              {formatDate(date)}
            </span>)}
          </div>}

          <div className='d-flex flex-column flex-md-row align-items-md-center gap-3'>
            {fields.length > 1 && <Dropdown>
              <Dropdown.Toggle
                as='span'
                className={`btn btn-sm rounded-pill text-nowrap px-4 ${selectedField ? 'btn-primary' : 'btn-light'}`}
              >
                <i className='las la-map-marker fs-5 me-1' />
                {fields.find((field: any) => field.id === selectedField)?.attributes?.name ?? 'Todas as quadras'}
              </Dropdown.Toggle>

              <Dropdown.Menu className='menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-700 menu-state-bg-light-primary menu-state-primary fw-semibold fs-7 w-250px py-3 mh-300px overflow-auto'>
                {[{ id: '', attributes: { name: 'Todas as quadras' } }, ...fields].map((field: any) => <div key={`field-${field.id}`} className='menu-item px-3'>
                  <Dropdown.Item
                    as='span'
                    className={`menu-link px-3 ${selectedField === field.id ? 'active' : ''}`}
                    onClick={() => setSelectedField(field.id)}
                  >
                    {field.attributes.name}
                  </Dropdown.Item>
                </div>)}
              </Dropdown.Menu>
            </Dropdown>}

            <div className='d-flex flex-wrap align-items-center gap-5 ms-md-auto'>
              <label className='form-check form-check-sm form-check-custom form-check-solid form-switch cursor-pointer'>
                <input
                  className='form-check-input h-20px w-35px'
                  type='checkbox'
                  checked={onlyAvailable}
                  onChange={() => setOnlyAvailable(!onlyAvailable)}
                />
                <span className='form-check-label fs-7 fw-semibold text-gray-700 ms-2'>
                  Somente com vagas
                </span>
              </label>

              <label className='form-check form-check-sm form-check-custom form-check-solid form-switch cursor-pointer'>
                <input
                  className='form-check-input h-20px w-35px'
                  type='checkbox'
                  checked={onlyMine}
                  onChange={toggleOnlyMine}
                />
                <span className='form-check-label fs-7 fw-semibold text-gray-700 ms-2'>
                  Minhas aulas
                </span>
              </label>
            </div>
          </div>
        </div>

        {loading && <LoadingContent />}

        {!loading && Object.keys(lessonsByDate).map((date: string) => <div key={`date-${date}`} className='mb-8'>
          <h3 className='mb-5'>
            {formatDate(date)}
          </h3>

          <div className='row g-5'>
            {lessonsByDate[date].map((lesson: any) => <div className='col-lg-6 d-flex' key={`lesson-${lesson.id}-${lesson.attributes.start_at}`}>
              <LessonItem
                lesson={lesson}
                className='h-100 mb-0'
                onClick={() => navigate(getLessonPath(lesson.id, lesson.attributes.date))}
              />
            </div>)}
          </div>
        </div>)}

        {!loading && filteredLessons.length === 0 && <div className='my-10 text-center'>
          <h2 className='mb-3'>
            {lessons.length > 0 && 'Nenhuma aula encontrada com esses filtros'}
            {lessons.length === 0 && (onlyMine ? 'Você não tem aulas nos próximos 7 dias' : 'Nenhuma aula nos próximos 7 dias')}
          </h2>
        </div>}
      </div>
    </div>
  );
}

export {
  LessonList,
}
